<script setup lang="ts">
import { USER_V2_FIELD_CLASS as fieldClass } from './types';
import type { TimeoutLimitsPayload } from '@/types/UserLimits';

// Tela "Pausa Temporária" do Minha Conta V2 (ENG-362).
// Visual: card escuro que expande (Figma desktop 19771:52574; mobile segue o
// padrão da tela de Auto-Exclusão). O fluxo reusa a lógica real da página
// antiga de Jogo Responsável (components/user/account/Protection/
// TimeoutLimits.vue): useUserLimits().setTimeOutLimit com validação de
// containment ('update_limits'), kyc_id e password_check.
const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { isAccountTimeoutLimitsEnabled } = useFeatureFlagsNew();
const { getDateTime } = useDateTimeHelper();
const { fetchUserProfile } = useUserData();

const { userLimits, setTimeOutLimit, userTimeoutMinOptionVal } =
  useUserLimits();

// tela de propósito único (só este card) — já chega expandida, senão o
// usuário precisaria clicar de novo pra ver o conteúdo da tela que abriu.
const isExpanded = ref(true);
const isLoading = ref(false);
const timeOutActive = ref<number>(0);
const timeOutPeriod = ref<string>('0');
const timeOutReason = ref<string>('0');

const choiceOptions = [
  { value: 0, label: 'Não' },
  { value: 1, label: 'Sim' },
];

const timeOutOptions = [
  { value: '0', label: t('account.timeout_limits.select_period') },
  { value: '1', label: '1 dia' },
  ...Array.from({ length: 44 }, (_, i) => ({
    value: (i + 2).toString(),
    label: `${i + 2} dias`,
  })),
];

const reasonOptions = [
  { value: '0', label: t('account.timeout_limits.reason_0') },
  { value: '1', label: t('account.timeout_limits.reason_1') },
  { value: '2', label: t('account.timeout_limits.reason_2') },
  { value: '3', label: t('account.timeout_limits.reason_3') },
  { value: '4', label: t('account.timeout_limits.reason_4') },
  { value: '5', label: t('account.timeout_limits.reason_5') },
  { value: '6', label: t('account.timeout_limits.reason_6') },
];

const timeOutOptionsFiltered = computed(() => {
  return timeOutOptions.filter(
    (option) =>
      Number(option.value) >= Number(userTimeoutMinOptionVal.value) ||
      option.value === '0',
  );
});

// pausa ativa não pode ser removida, só estendida
const isDisabledRemoveTimeOut = computed(() => {
  return !!userLimits.value?.user_pause;
});

const isDisabledTimeOutButton = computed(() => {
  return (
    isLoading.value ||
    !timeOutPeriod.value ||
    !timeOutReason.value ||
    timeOutPeriod.value === '0' ||
    timeOutReason.value === '0'
  );
});

const timeOutButtonText = computed(() => {
  if (timeOutPeriod.value === '0') {
    return t('account.timeout_limits.select_period');
  }

  if (timeOutReason.value === '0') {
    return t('account.timeout_limits.reason_0');
  }

  if (timeOutPeriod.value === '1') {
    return t('account.timeout_limits.pause_for_1_day');
  }

  return t('account.account_protection.button_save', {
    period: timeOutPeriod.value,
  });
});

async function handleSubmit() {
  if (timeOutPeriod.value === '0' || !timeOutPeriod.value) {
    openToaster({
      message: t('account.timeout_limits.select_period'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  if (timeOutReason.value === '0' || !timeOutReason.value) {
    openToaster({
      message: t('account.timeout_limits.reason_0'),
      type: 'error',
      time: 10000,
    });

    return;
  }

  const { handleUpdateLimitsForcingKyc } = useModulesValidation();

  await handleUpdateLimitsForcingKyc(() => {
    sendSubmit();
  });
}

async function sendSubmit() {
  if (!Number(timeOutPeriod.value) || !Number(timeOutReason.value)) {
    return;
  }

  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  const payload: TimeoutLimitsPayload = {
    user_pause: Number(timeOutPeriod.value),
    reason: Number(timeOutReason.value),
  };

  if ($kyc?.current?.id) {
    payload.kyc_id = $kyc.current.id;
  }

  if (temporaryPassword.get()) {
    payload.password_check = temporaryPassword.get();
  }

  await setTimeOutLimit(payload)
    .then((res: any) => {
      const message =
        res.data?.message || t('account.timeout_limits.update_success');

      openToaster({
        message,
        type: 'success',
      });
    })
    .catch((error: any) => {
      const message =
        error.data?.message ||
        error.message ||
        t('account.timeout_limits.generic_error');

      openToaster({
        message,
        type: 'error',
        time: 12000,
      });
    })
    .finally(() => {
      isLoading.value = false;
      temporaryPassword.clear();
    });
}

watch(
  userLimits,
  () => {
    if (userLimits.value?.user_pause) {
      timeOutActive.value = 1;
      timeOutPeriod.value = String(userLimits.value.user_pause);
    } else {
      timeOutActive.value = 0;
    }
  },
  { immediate: true },
);

onBeforeMount(async () => {
  if (!userLimits.value?.user_pause_at) {
    await fetchUserProfile({ force: true });
  }
});
</script>

<template>
  <div class="flex flex-col gap-[8px]">
    <!-- card "Pausa Temporária" (accordion) -->
    <button
      class="flex w-full items-center gap-[8px] rounded-[10px] bg-v2-default/40 p-[12px]"
      type="button"
      @click="isExpanded = !isExpanded"
    >
      <span
        class="flex size-[32px] shrink-0 items-center justify-center rounded-[10px] bg-v2-default/40"
      >
        <UserV2Icon name="pause" />
      </span>
      <span class="flex h-[36px] min-w-0 flex-1 items-center text-left">
        <span class="text-[14px] font-medium leading-[20px] text-white">
          Pausa Temporária
        </span>
      </span>
      <UserV2Icon
        :class="{ 'rotate-180': isExpanded }"
        name="chevron-down"
      />
    </button>

    <div
      v-if="isExpanded"
      class="flex flex-col gap-[16px] rounded-[10px] bg-v2-default/40 p-[16px]"
    >
      <p
        v-if="!isAccountTimeoutLimitsEnabled"
        class="rounded-[12px] bg-v2-danger/20 p-[12px] text-[12px] leading-[16px] text-v2-danger"
      >
        {{ t('error.page_not_found.subtitle') }}
      </p>

      <template v-else>
        <p class="text-[12px] leading-[16px] text-v2-default-900">
          {{ t('account.timeout_limits.text') }}
        </p>

        <div class="flex flex-col gap-[8px]">
          <p class="text-[12px] leading-[16px] text-v2-default-700">
            {{ t('account.timeout_limits.set_timeout') }}
          </p>
          <div class="flex gap-[8px]">
            <button
              v-for="option in choiceOptions"
              :key="option.value"
              class="h-[32px] rounded-[12px] px-[16px] text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
              :class="
                timeOutActive === option.value
                  ? 'bg-v2-primary text-white'
                  : 'bg-v2-default/40 text-v2-foreground'
              "
              :disabled="isDisabledRemoveTimeOut"
              type="button"
              @click="timeOutActive = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </div>

        <template v-if="timeOutActive !== 0">
          <div class="flex flex-col gap-[4px]">
            <label
              class="text-[12px] leading-[16px] text-v2-default-700"
              for="temporary-pause-period"
            >
              {{ t('account.timeout_limits.pause_for') }}
            </label>
            <select
              id="temporary-pause-period"
              v-model="timeOutPeriod"
              :class="fieldClass"
            >
              <option
                v-for="option in timeOutOptionsFiltered"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>

          <div class="flex flex-col gap-[4px]">
            <label
              class="text-[12px] leading-[16px] text-v2-default-700"
              for="temporary-pause-reason"
            >
              {{ t('account.timeout_limits.reason') }}
            </label>
            <select
              id="temporary-pause-reason"
              v-model="timeOutReason"
              :class="fieldClass"
            >
              <option
                v-for="option in reasonOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </template>

        <p
          v-if="userLimits?.user_pause"
          class="text-[12px] leading-[16px] text-v2-warning/70"
        >
          {{ t('account.account_protection.last_change') }}
          <strong>{{ getDateTime(userLimits?.user_pause_at) }}</strong>
        </p>

        <button
          v-if="timeOutActive"
          class="flex h-[40px] w-full items-center justify-center gap-[8px] rounded-[12px] bg-v2-primary text-[14px] text-white disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isDisabledTimeOutButton"
          type="button"
          @click="handleSubmit"
        >
          {{ timeOutButtonText }}
        </button>
      </template>
    </div>
  </div>
</template>
